import { useMemo } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { type CustomerSlaBreakdownResponse, Permission } from '@metron/shared';
import { usePermissions } from '../hooks/usePermissions.ts';
import { useApiClient } from '../hooks/useApiClient.ts';
import { useCustomers } from '../hooks/useCustomers.ts';
import { useCustomerRevenue } from '../hooks/useCustomerRevenue.ts';
import { CustomerRevenueSummary, REVENUE_CAPTION } from '../components/CustomerRevenue.tsx';
import { UsersTable } from '../components/users-table/UsersTable.tsx';
import type { UsersTableScope } from '../components/users-table/types.ts';
import { CustomerMetersTab } from '../components/customer-details/CustomerMetersTab.tsx';
import { CustomerPropertiesTab } from '../components/customer-details/CustomerPropertiesTab.tsx';
import { CustomerConsumersTab } from '../components/customer-details/CustomerConsumersTab.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { Button } from '../components/ui/button.tsx';
import { ArrowLeft } from 'lucide-react';
import { InstalledMetersTooltip } from '../components/InstalledMetersTooltip.tsx';
import { cn, sla1dColorClass, sla3dColorClass } from '../lib/utils.ts';
import {
  type CustomerDetailsTab,
  DEFAULT_CUSTOMER_DETAILS_TAB,
  parseCustomerDetailsTab,
  visibleCustomerDetailsTabs,
} from './customerDetailsTabs.ts';

/**
 * Customer Details Page
 *
 * Central hub for managing all aspects of a customer, split into tabs:
 * Customer Details (info, SLA and revenue cards), Meters, Properties, Users
 * and Consumers. The selected tab rides the URL (`?tab=`); Customer Details is
 * the default and stays out of it.
 */
function formatSla(value: number | null): string {
  if (value === null) return '\u2014';
  return `${value.toFixed(2)}%`;
}

export function CustomerDetailsPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { customers, isLoading, error } = useCustomers();
  const { hasPermission, isViewer } = usePermissions();
  const canViewRevenue = hasPermission(Permission.VIEW_REVENUE);
  const {
    data: revenueData,
    isLoading: revenueLoading,
    isError: revenueError,
  } = useCustomerRevenue(canViewRevenue);

  const [searchParams, setSearchParams] = useSearchParams();
  const visibleTabs = visibleCustomerDetailsTabs({ hasPermission, isViewer });
  const tab = parseCustomerDetailsTab(searchParams.get('tab'), visibleTabs);

  // Switching tabs only touches `tab`. Each table keeps its page and sort under
  // its own URL keys (unprefixed for Meters, `users*`, `consumers*`), so those
  // survive a round trip; state held inside a tab (the consumers search box,
  // unsaved property edits) resets when the tab unmounts, as it did when these
  // were separate pages. Tab changes push a history entry so Back returns to
  // the previous tab, matching the old page-to-page navigation.
  const setTabParam = (value: CustomerDetailsTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === DEFAULT_CUSTOMER_DETAILS_TAB) {
        next.delete('tab');
      } else {
        next.set('tab', value);
      }
      return next;
    });
  };

  const { data: slaBreakdown } = useQuery({
    queryKey: ['read-rate-sla', 'by-customer'],
    queryFn: () => apiClient.get<CustomerSlaBreakdownResponse>('read-rate-sla/by-customer'),
  });

  const customerSla = useMemo(() => {
    if (!slaBreakdown || !customerId) return null;
    return slaBreakdown.data.find((row) => row.customerId === Number(customerId)) ?? null;
  }, [slaBreakdown, customerId]);

  const customer = useMemo(() => {
    if (!customerId) return null;
    return customers.find((c) => c.id === Number(customerId));
  }, [customers, customerId]);

  const customerRevenue = useMemo(
    () => revenueData?.customers.find((r) => r.customerId === Number(customerId)),
    [revenueData, customerId],
  );

  if (isLoading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-muted-foreground'>Loading customer...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-destructive'>Error: {error}</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-destructive'>Customer not found</p>
      </div>
    );
  }

  // The same table `/users` mounts, scoped to this customer: it hides the
  // Customer column and the customer filter, keeps only the rows that belong
  // here, and locks the customer in its own Create/Edit dialogs — so the tab
  // below needs no Create User button and no dialog of its own. The table
  // memoises the scope internally, so a fresh literal per render is fine.
  const usersScope: UsersTableScope = { mode: 'customer', customerId: customer.id };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <Button
          variant='ghost'
          onClick={() => navigate('/customers')}
        >
          <ArrowLeft className='h-4 w-4 mr-2' />
          Back to Customers
        </Button>
      </div>

      <div>
        <h1 className='text-3xl font-bold tracking-tight'>{customer.waterscopeAccountName}</h1>
        <p className='text-muted-foreground mt-2'>
          Manage customer information and settings
        </p>
      </div>

      <div>
        <div
          role='tablist'
          aria-label='Customer sections'
          className='mb-4 flex items-center gap-1 border-b'
        >
          {visibleTabs.map((t) => {
            const selected = t.id === tab;
            return (
              <button
                key={t.id}
                type='button'
                role='tab'
                id={`customer-tab-${t.id}`}
                aria-selected={selected}
                aria-controls={selected ? 'customer-tabpanel' : undefined}
                onClick={() => setTabParam(t.id)}
                className={cn(
                  '-mb-px inline-flex h-9 items-center border-b-2 px-3 text-sm font-medium transition-colors',
                  selected
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <div role='tabpanel' id='customer-tabpanel' aria-labelledby={`customer-tab-${tab}`}>
          {tab === 'details' && (
            <div className='space-y-4'>
              <div className='grid gap-4 md:grid-cols-2'>
                <Card>
                  <CardHeader>
                    <CardTitle>Customer Information</CardTitle>
                    <CardDescription>Basic customer details</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className='grid grid-cols-2 gap-4'>
                      <div>
                        <p className='text-sm font-medium text-muted-foreground'>ID</p>
                        <p className='text-sm'>{customer.id}</p>
                      </div>
                      <div>
                        <p className='text-sm font-medium text-muted-foreground'>WaterScope ID</p>
                        <p className='text-sm'>{customer.waterscopeAccountId || 'N/A'}</p>
                      </div>
                      <div>
                        <p className='text-sm font-medium text-muted-foreground'>Account Type</p>
                        <p className='text-sm'>{customer.accountType}</p>
                      </div>
                      <div>
                        <p className='text-sm font-medium text-muted-foreground'>
                          Real Estate Type
                        </p>
                        <p className='text-sm'>{customer.realEstateAccountType || 'N/A'}</p>
                      </div>
                      <div>
                        <p className='text-sm font-medium text-muted-foreground'>Salesforce ID</p>
                        <p className='text-sm'>{customer.salesforceId || 'N/A'}</p>
                      </div>
                      <div>
                        <p className='text-sm font-medium text-muted-foreground'>Metron ID</p>
                        <p className='text-sm'>
                          {customer.metronId
                            ? <code className='text-xs'>{customer.metronId}</code>
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className='text-sm font-medium text-muted-foreground'>
                          <InstalledMetersTooltip>Installed</InstalledMetersTooltip>
                        </p>
                        <p className='text-sm font-medium'>
                          {customer.installedSensorCount.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className='text-sm font-medium text-muted-foreground'>Total</p>
                        <p className='text-sm font-medium'>
                          {customer.sensorCount.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {customerSla && (
                  <Card>
                    <CardHeader>
                      <CardTitle>SLA</CardTitle>
                      <CardDescription>
                        As of {slaBreakdown?.asOfDate ?? ''}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className='space-y-3'>
                      <div className='grid grid-cols-2 gap-4'>
                        <div>
                          <p className='text-sm font-medium text-muted-foreground'>1-Day SLA</p>
                          <p
                            className={`text-lg font-semibold ${
                              sla1dColorClass(customerSla.sla1d)
                            }`}
                          >
                            {formatSla(customerSla.sla1d)}
                          </p>
                          <p className='text-xs text-muted-foreground'>
                            {customerSla.sensorsReported1d} / {customerSla.installedSensors30d}{' '}
                            sensors
                          </p>
                        </div>
                        <div>
                          <p className='text-sm font-medium text-muted-foreground'>3-Day SLA</p>
                          <p
                            className={`text-lg font-semibold ${
                              sla3dColorClass(customerSla.sla3d)
                            }`}
                          >
                            {formatSla(customerSla.sla3d)}
                          </p>
                          <p className='text-xs text-muted-foreground'>
                            {customerSla.sensorsReported3d} / {customerSla.installedSensors30d}{' '}
                            sensors
                          </p>
                        </div>
                      </div>
                      {customerSla.waveTotalSensors30d > 0 && (() => {
                        const wt = customerSla.waveTotalSensors30d;
                        const waveSla1d =
                          Math.round((customerSla.waveSensorsReported1d / wt) * 10000) /
                          100;
                        const waveSla3d =
                          Math.round((customerSla.waveSensorsReported3d / wt) * 10000) /
                          100;
                        return (
                          <>
                            <hr className='border-border' />
                            <p className='text-sm font-medium text-muted-foreground'>
                              Wave / Astria
                            </p>
                            <div className='grid grid-cols-2 gap-4'>
                              <div>
                                <p className='text-sm font-medium text-muted-foreground'>
                                  1-Day SLA
                                </p>
                                <p
                                  className={`text-lg font-semibold ${sla1dColorClass(waveSla1d)}`}
                                >
                                  {formatSla(waveSla1d)}
                                </p>
                                <p className='text-xs text-muted-foreground'>
                                  {customerSla.waveSensorsReported1d} / {wt} sensors
                                </p>
                              </div>
                              <div>
                                <p className='text-sm font-medium text-muted-foreground'>
                                  3-Day SLA
                                </p>
                                <p
                                  className={`text-lg font-semibold ${sla3dColorClass(waveSla3d)}`}
                                >
                                  {formatSla(waveSla3d)}
                                </p>
                                <p className='text-xs text-muted-foreground'>
                                  {customerSla.waveSensorsReported3d} / {wt} sensors
                                </p>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </CardContent>
                  </Card>
                )}
              </div>

              {canViewRevenue && (
                <Card>
                  <CardHeader>
                    <CardTitle>Revenue</CardTitle>
                    <CardDescription>{REVENUE_CAPTION}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {revenueLoading
                      ? <p className='text-sm text-muted-foreground'>Loading revenue...</p>
                      : revenueError
                      ? <p className='text-sm text-destructive'>Failed to load revenue</p>
                      : (
                        <CustomerRevenueSummary
                          revenue={customerRevenue}
                        />
                      )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          {tab === 'meters' && <CustomerMetersTab customer={customer} />}
          {tab === 'properties' && <CustomerPropertiesTab customerId={customer.id} />}
          {tab === 'users' && (
            <Card>
              <CardHeader>
                <CardTitle>Plus Users</CardTitle>
                <CardDescription>
                  Users associated with this customer.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <UsersTable scope={usersScope} />
              </CardContent>
            </Card>
          )}
          {tab === 'consumers' && <CustomerConsumersTab customerId={String(customer.id)} />}
        </div>
      </div>
    </div>
  );
}

/**
 * The old `/customers/:customerId/properties` and `/end-consumers` pages are
 * now tabs here; this keeps their bookmarks and inbound links working.
 */
export function CustomerTabRedirect({ tab }: { tab: CustomerDetailsTab }) {
  const { customerId } = useParams<{ customerId: string }>();
  return <Navigate to={`/customers/${customerId}?tab=${tab}`} replace />;
}
